import { toWSDateString } from "@wingspanhq/fe-component-library";
import { PayrollRun } from "@wingspanhq/wingspan-api-sdk";

import { selectorWorkLogApprovalDaysLeft } from "./selectorWorkLogApprovalDaysLeft";

export type WorkLogApprovalWindow = {
  /** `warning` when approving can no longer reach a run for this pay period. */
  theme: "info" | "warning";
  title: string;
  message: string;
};

/**
 * Explains what approving a work log will (or won't) do, for the approve
 * confirmation modal.
 *
 * Three cases from the design:
 *  - an open run for the period, deadline still ahead → the work log joins that
 *    run and pays out on its pay date, if approved within N days;
 *  - an open run whose approval deadline has passed → it can no longer be
 *    associated, so an off-cycle run is needed;
 *  - no run for the period at all → same, there is nothing to join.
 */
export const selectorWorkLogApprovalWindow = ({
  payrollRun,
  now = new Date()
}: {
  payrollRun: PayrollRun | undefined;
  now?: Date;
}): WorkLogApprovalWindow => {
  if (!payrollRun) {
    return {
      theme: "warning",
      title: "No payroll run for this pay period",
      message:
        "There is no open payroll run covering this pay period, so approving won't schedule a payment. To pay this work log you'll need to create an off-cycle payroll run."
    };
  }

  const runLabel = payrollRun.payrollNumber
    ? `payroll run #${payrollRun.payrollNumber}`
    : "the open payroll run";
  const deadline = payrollRun.approvalDeadline ?? undefined;

  if (deadline && deadline.getTime() <= now.getTime()) {
    return {
      theme: "warning",
      title: "Approval window has closed",
      message: `The approval window for ${runLabel} closed on ${toWSDateString(
        deadline,
        "monthDayYear"
      )}, so this work log can no longer be added to it. To pay it you'll need to create an off-cycle payroll run.`
    };
  }

  const paysOut = payrollRun.payDate
    ? ` and pay out on ${toWSDateString(payrollRun.payDate, "monthDayYear")}`
    : "";

  if (!deadline) {
    return {
      theme: "info",
      title: "About approved work logs",
      message: `Once approved, this work log is added to ${runLabel}${paysOut}.`
    };
  }

  const daysLeft = selectorWorkLogApprovalDaysLeft({ payrollRun, now }) ?? 0;

  return {
    theme: "info",
    title: "About approved work logs",
    message: `Approve within ${daysLeft} ${
      daysLeft === 1 ? "day" : "days"
    } (by ${toWSDateString(
      deadline,
      "monthDayYear"
    )}) and this work log is added to ${runLabel}${paysOut}. After that it can only be paid through an off-cycle payroll run.`
  };
};
